import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken && !error.config._retry) {
        error.config._retry = true;
        try {
          const res = await axios.post(`${API_BASE}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });
          localStorage.setItem('access_token', res.data.access_token);
          error.config.headers.Authorization = `Bearer ${res.data.access_token}`;
          return api(error.config);
        } catch {
          localStorage.clear();
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updateMe: (data) => api.put('/auth/me', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
};

// Admin
export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  approveUser: (id) => api.post(`/admin/users/${id}/approve`),
  rejectUser: (id, data) => api.post(`/admin/users/${id}/reject`, data),
  suspendUser: (id) => api.post(`/admin/users/${id}/suspend`),
  getPendingTargets: (params) => api.get('/admin/targets/pending', { params }),
  getAllTargets: (params) => api.get('/admin/targets/all', { params }),
  publishTarget: (id) => api.post(`/admin/targets/${id}/publish`),
  rejectTarget: (id) => api.post(`/admin/targets/${id}/reject`),
  getPendingIdeas: (params) => api.get('/admin/ideas/pending', { params }),
  approveIdea: (id) => api.post(`/admin/ideas/${id}/approve`),
  rejectIdea: (id) => api.post(`/admin/ideas/${id}/reject`),
  featureIdea: (id) => api.post(`/admin/ideas/${id}/feature`),
  getPendingReports: (params) => api.get('/admin/reports/pending', { params }),
  approveReport: (id) => api.post(`/admin/reports/${id}/approve`),
  rejectReport: (id, data) => api.post(`/admin/reports/${id}/reject`, data),
  getDisbursements: (params) => api.get('/admin/disbursements', { params }),
  createDisbursement: (data) => api.post('/admin/disbursements', data),
  approveDisbursement: (id) => api.post(`/admin/disbursements/${id}/approve`),
  disburseFunds: (id) => api.post(`/admin/disbursements/${id}/disburse`),
  rejectDisbursement: (id, data) => api.post(`/admin/disbursements/${id}/reject`, data),
  updateDisbursement: (id, data) => api.put(`/admin/disbursements/${id}`, data),
  deleteDisbursement: (id) => api.delete(`/admin/disbursements/${id}`),
  getBlogs: (params) => api.get('/admin/blog', { params }),
  getBlog: (id) => api.get(`/admin/blog/${id}`),
  createBlog: (data) => api.post('/admin/blog', data),
  updateBlog: (id, data) => api.put(`/admin/blog/${id}`, data),
  publishBlog: (id) => api.post(`/admin/blog/${id}/publish`),
  archiveBlog: (id) => api.post(`/admin/blog/${id}/archive`),
  deleteBlog: (id) => api.delete(`/admin/blog/${id}`),
  getPendingBlogs: (params) => api.get('/admin/blog/pending', { params }),
  approveBlog: (id) => api.post(`/admin/blog/${id}/approve`),
  rejectBlog: (id, data) => api.post(`/admin/blog/${id}/reject`, data),
  getPendingGroupMonitoring: (params) => api.get('/admin/group-monitoring/pending', { params }),
  getGroupMonitoringDetail: (id) => api.get(`/admin/group-monitoring/${id}`),
  approveGroupMonitoring: (id) => api.post(`/admin/group-monitoring/${id}/approve`),
  rejectGroupMonitoring: (id, data) => api.post(`/admin/group-monitoring/${id}/reject`, data),
  getMonitoringSummary: () => api.get('/admin/monitoring/summary'),
  getTargetDetail: (id) => api.get(`/admin/targets/${id}/detail`),
  updateZone: (id, data) => api.put(`/admin/monitoring/zones/${id}`, data),
  deleteZone: (id) => api.delete(`/admin/monitoring/zones/${id}`),
  // Allocations (Project-Agency assignments)
  getAllocations: (params) => api.get('/admin/allocations', { params }),
  createAllocation: (data) => api.post('/admin/allocations', data),
  updateAllocation: (id, data) => api.put(`/admin/allocations/${id}`, data),
  deleteAllocation: (id) => api.delete(`/admin/allocations/${id}`),
  getAgencies: () => api.get('/admin/agencies'),
  getTargetsList: () => api.get('/admin/targets-list'),
  // Zones (admin-level)
  getZones: (params) => api.get('/admin/zones', { params }),
  createZone: (data) => api.post('/admin/zones', data),
  // Field Supervisors
  getFieldSupervisors: (params) => api.get('/admin/field-supervisors', { params }),
  createFieldSupervisor: (data) => api.post('/admin/field-supervisors', data),
};

// CSR
export const csrAPI = {
  getDashboard: () => api.get('/csr/dashboard'),
  getTargets: (params) => api.get('/csr/targets', { params }),
  createTarget: (data) => api.post('/csr/targets', data),
  getTarget: (id) => api.get(`/csr/targets/${id}`),
  updateTarget: (id, data) => api.put(`/csr/targets/${id}`, data),
  getAgencies: (params) => api.get('/csr/agencies', { params }),
  getAgency: (id) => api.get(`/csr/agencies/${id}`),
  acceptApplication: (targetId, appId) => api.post(`/csr/targets/${targetId}/applications/${appId}/accept`),
  rejectApplication: (targetId, appId) => api.post(`/csr/targets/${targetId}/applications/${appId}/reject`),
  getLegacy: (params) => api.get('/csr/legacy', { params }),
  uploadLegacy: (data) => api.post('/csr/legacy', data),
  getMyIdeas: (params) => api.get('/csr/ideas', { params }),
  postIdea: (data) => api.post('/csr/ideas', data),
  getMonitoringSummary: () => api.get('/csr/monitoring/summary'),
  getMonitoringEntries: (params) => api.get('/csr/monitoring/entries', { params }),
  getMonitoringEntry: (id) => api.get(`/csr/monitoring/entries/${id}`),
  getDisbursements: (params) => api.get('/csr/disbursements', { params }),
  createDisbursement: (data) => api.post('/csr/disbursements', data),
  getAllocations: () => api.get('/csr/allocations'),
  getMyBlogs: (params) => api.get('/csr/blog', { params }),
  createBlog: (data) => api.post('/csr/blog', data),
  updateBlog: (id, data) => api.put(`/csr/blog/${id}`, data),
  deleteBlog: (id) => api.delete(`/csr/blog/${id}`),
};

// Individual
export const individualAPI = {
  getDashboard: () => api.get('/individual/dashboard'),
  getSponsorships: (params) => api.get('/individual/sponsorships', { params }),
  createSponsorship: (data) => api.post('/individual/sponsorships', data),
  getSponsorship: (id) => api.get(`/individual/sponsorships/${id}`),
  getVolunteering: (params) => api.get('/individual/volunteering', { params }),
  signupVolunteering: (id) => api.post(`/individual/volunteering/${id}/signup`),
  getMySignups: () => api.get('/individual/volunteering/my-signups'),
  getMyIdeas: (params) => api.get('/individual/ideas', { params }),
  postIdea: (data) => api.post('/individual/ideas', data),
  completePayment: (id, data) => api.post(`/individual/payments/${id}/complete`, data),
  getMyBlogs: (params) => api.get('/individual/blog', { params }),
  createBlog: (data) => api.post('/individual/blog', data),
  updateBlog: (id, data) => api.put(`/individual/blog/${id}`, data),
  deleteBlog: (id) => api.delete(`/individual/blog/${id}`),
  getMonitoringSummary: () => api.get('/individual/monitoring/summary'),
};

// Agency
export const agencyAPI = {
  getDashboard: () => api.get('/agency/dashboard'),
  getOpportunities: (params) => api.get('/agency/opportunities', { params }),
  getOpportunity: (id) => api.get(`/agency/opportunities/${id}`),
  applyOpportunity: (id, data) => api.post(`/agency/opportunities/${id}/apply`, data),
  getApplications: (params) => api.get('/agency/applications', { params }),
  getProjects: (params) => api.get('/agency/projects', { params }),
  getProject: (id) => api.get(`/agency/projects/${id}`),
  submitReport: (data) => api.post('/agency/reports', data),
  uploadReportDocument: (reportId, formData) => api.post(`/agency/reports/${reportId}/document`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getReports: (params) => api.get('/agency/reports', { params }),
  getPayments: (params) => api.get('/agency/payments', { params }),
  getMyIdeas: (params) => api.get('/agency/ideas', { params }),
  postIdea: (data) => api.post('/agency/ideas', data),
  getMyBlogs: (params) => api.get('/agency/blog', { params }),
  createBlog: (data) => api.post('/agency/blog', data),
  updateBlog: (id, data) => api.put(`/agency/blog/${id}`, data),
  deleteBlog: (id) => api.delete(`/agency/blog/${id}`),
  getMonitoringSummary: () => api.get('/agency/monitoring/summary'),
  // Field Supervisors
  getFieldSupervisors: () => api.get('/agency/field-supervisors'),
  createFieldSupervisor: (data) => api.post('/agency/field-supervisors', data),
  deleteFieldSupervisor: (id) => api.delete(`/agency/field-supervisors/${id}`),
  updateSupervisorAssignments: (id, data) => api.put(`/agency/field-supervisors/${id}/assignments`, data),
};

// Group Monitoring
export const monitoringAPI = {
  getZones: (params) => api.get('/monitoring/zones', { params }),
  createZone: (data) => api.post('/monitoring/zones', data),
  getZone: (id) => api.get(`/monitoring/zones/${id}`),
  updateZone: (id, data) => api.put(`/monitoring/zones/${id}`, data),
  submitEntry: (data) => api.post('/monitoring/entries', data),
  getEntries: (params) => api.get('/monitoring/entries', { params }),
  getEntry: (id) => api.get(`/monitoring/entries/${id}`),
  uploadPhoto: (entryId, formData) => api.post(`/monitoring/entries/${entryId}/photos`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
};

// Adoption
export const adoptionAPI = {
  getSites: (params) => api.get('/adoption/sites', { params }),
  getSite: (id) => api.get(`/adoption/sites/${id}`),
  createAdoption: (data) => api.post('/adoption/adopt', data),
  getMyAdoptions: (params) => api.get('/adoption/my-adoptions', { params }),
  getAdoption: (id) => api.get(`/adoption/my-adoptions/${id}`),
  getAgencySites: (params) => api.get('/adoption/agency/sites', { params }),
  submitMaintenanceReport: (data) => api.post('/adoption/agency/reports', data),
  getAgencyReports: (params) => api.get('/adoption/agency/reports', { params }),
};

// Public
export const publicAPI = {
  getMetrics: () => api.get('/public/metrics'),
  getExtendedMetrics: () => api.get('/public/metrics/extended'),
  getIdeas: (params) => api.get('/public/ideas', { params }),
  getPlantationMap: () => api.get('/public/plantation-map'),
  getVolunteering: () => api.get('/public/volunteering'),
  getContributors: () => api.get('/public/contributors'),
  getContributorDetail: (id) => api.get(`/public/contributors/${id}`),
  getBlogPosts: (params) => api.get('/public/blog', { params }),
  getBlogPost: (id) => api.get(`/public/blog/${id}`),
  validateReferral: (code) => api.get(`/auth/validate-referral`, { params: { code } }),
};

// Species catalog + project allocations
export const speciesAPI = {
  list: (params) => api.get('/species/', { params }),
  targetSpecies: (targetId) => api.get(`/species/targets/${targetId}`),
  report: () => api.get('/species/report'),
  // Admin CRUD
  create: (data) => api.post('/species/admin', data),
  update: (id, data) => api.put(`/species/admin/${id}`, data),
  uploadImage: (id, formData) => api.post(`/species/admin/${id}/image`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  delete: (id) => api.delete(`/species/admin/${id}`),
  addAllocation: (targetId, data) => api.post(`/species/admin/targets/${targetId}`, data),
  updateAllocation: (allocId, data) => api.put(`/species/admin/allocations/${allocId}`, data),
  deleteAllocation: (allocId) => api.delete(`/species/admin/allocations/${allocId}`),
};

// Notifications
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications/', { params }),
  markRead: (id) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

export default api;
